import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import auditLogService from '../services/auditLogService';
import retryService from '../services/retryService';
import notificationManager from '../services/notificationManager';
import recoveryService from '../services/recoveryService';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId) {
      if (!userId) return
      setProfileLoading(true)
      try {
        const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single()
        if (!error) setUserProfile(data)
      } catch (error) {
        console.error('Profile load error:', error)
      } finally {
        setProfileLoading(false)
      }
    },

    clear() {
      setUserProfile(null)
      setProfileLoading(false)
    }
  }

  // Auth state handlers - PROTECTED from async modification
  const authStateHandlers = {
    // This handler MUST remain synchronous - Supabase requirement
    onChange: (event, session) => {
      setUser(session?.user ?? null)
      setLoading(false)
      
      if (session?.user) {
        profileOperations?.load(session?.user?.id) // Fire-and-forget
      } else {
        profileOperations?.clear()
      }
    }
  }

  useEffect(() => {
    // Initial session check
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      authStateHandlers?.onChange(null, session)
    })

    // CRITICAL: This must remain synchronous
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    )

    return () => subscription?.unsubscribe()
  }, [])

  // Auth methods
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      
      // Log successful login
      if (data?.user && !error) {
        await auditLogService?.logLogin(data?.user?.id, {
          timestamp: new Date()?.toISOString()
        });
      }
      
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signUp = async (email, password, profileData) => {
    try {
      // Create auth user
      const { data: authData, error: authError } = await supabase?.auth?.signUp({
        email,
        password
      })

      if (authError) return { error: authError }

      // Create user profile with all onboarding data
      const { data: profileDataResult, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.insert({
          id: authData?.user?.id,
          email: email,
          full_name: `${profileData?.firstName} ${profileData?.lastName}`,
          date_of_birth: profileData?.dateOfBirth,
          gender: profileData?.gender,
          relationship_status: profileData?.relationshipStatus,
          relationship_duration: profileData?.relationshipDuration,
          share_mood_data: profileData?.shareMoodData,
          share_availability: profileData?.shareAvailability,
          share_preferences: profileData?.sharePreferences,
          enable_ai_mediation: profileData?.enableAIMediation,
          allow_compatibility_analysis: profileData?.allowCompatibilityAnalysis,
          receive_ai_suggestions: profileData?.receiveAISuggestions,
          notify_mood_changes: profileData?.notifyMoodChanges,
          notify_schedule_reminders: profileData?.notifyScheduleReminders,
          notify_ai_messages: profileData?.notifyAIMessages
        })
        ?.select()
        ?.single()

      if (profileError) {
        console.error('Profile creation error:', profileError)
        return { error: profileError }
      }

      return { data: { user: authData?.user, profile: profileDataResult }, error: null }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      // Log logout before signing out
      if (user?.id) {
        await auditLogService?.logLogout(user?.id, {
          timestamp: new Date()?.toISOString()
        });
      }
      
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        setUser(null)
        profileOperations?.clear()
      }
      return { error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) {
      console.error('updateProfile: No user logged in')
      notificationManager?.error('No user logged in');
      return { error: { message: 'No user logged in' } }
    }
    
    console.log('updateProfile: Starting update for user:', user?.id)
    console.log('updateProfile: Updates to apply:', updates)
    
    // Cache the update for recovery
    recoveryService?.cacheProfileUpdate(user?.id, updates);
    
    // Get current profile for comparison
    const oldProfile = { ...userProfile };
    
    // Define the update operation
    const performUpdate = async () => {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.update(updates)
        ?.eq('id', user?.id)
        ?.select()
        ?.single();
      
      if (error) {
        throw error;
      }
      
      return { data, error: null };
    };
    
    // Execute with retry mechanism
    const result = await retryService?.retryProfileUpdate(
      performUpdate,
      updates,
      {
        maxRetries: 3,
        onRetry: (error, attempt, delay) => {
          console.log(`🔄 Retry attempt ${attempt} after ${delay}ms`);
          notificationManager?.info(
            `Retrying save operation (attempt ${attempt}/3)...`,
            { duration: 3000 }
          );
        },
        onFailure: (error, attempt) => {
          console.error(`❌ All ${attempt} retry attempts failed`);
          notificationManager?.error(
            'Failed to save profile after multiple attempts. Your changes are cached and will be retried.',
            {
              duration: 10000,
              action: {
                label: 'Retry Now',
                onClick: () => updateProfile(updates)
              }
            }
          );
          
          // Log failed update
          auditLogService?.logFailedAction(
            user?.id,
            'profile_updated',
            error?.message || 'Unknown error',
            { updates, attempts: attempt, timestamp: new Date()?.toISOString() }
          );
        }
      }
    );
    
    console.log('updateProfile: Retry result:', result);
    
    if (!result?.success) {
      return { data: null, error: result?.error };
    }
    
    // Success - clear cache and update state
    const { data } = result;
    
    if (data) {
      console.log('updateProfile: Setting new profile data:', data);
      setUserProfile(data);
      
      // Clear cached update on success
      recoveryService?.clearProfileCache(user?.id);
      
      // Detect changes and log to audit
      const changes = auditLogService?.detectChanges(oldProfile, data);
      if (changes?.changedFields?.length > 0) {
        await auditLogService?.logProfileUpdate(
          user?.id,
          changes?.oldValues,
          changes?.newValues,
          changes?.changedFields,
          { timestamp: new Date()?.toISOString() }
        );
      }
      
      notificationManager?.success('Profile updated successfully!');
    } else {
      console.warn('updateProfile: No data returned from database');
    }
    
    return { data, error: null };
  }

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signUp,
    signOut,
    updateProfile,
    isAuthenticated: !!user
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
